/**
 * Dragging a Dashplan block: moving it, and resizing it from either edge.
 *
 * One pointer-event code path serves mouse and touch, but the *activation*
 * rules have to differ, because the same gesture means different things on the
 * two devices:
 *
 * - A resize handle is a deliberate target. It drags immediately, whatever the
 *   input.
 * - Dragging the body is ambiguous. With a mouse it competes with a click, so
 *   it waits for a few pixels of travel. With a finger it also competes with
 *   scrolling the page, which no pixel threshold can tell apart — so it waits
 *   for a press long enough that it cannot have been a swipe.
 *
 * Nothing is written until release: a drag is one mutation, not one per pixel.
 */
import { useEffect, useRef, useState } from "react";

export type GestureMode = "move" | "resize-start" | "resize-end";

export interface Span {
  startMinute: number;
  durationMinutes: number;
}

/** Blocks land on five-minute boundaries, as they do when typed. */
export const SNAP_MINUTES = 5;

/** Below this a block has no room to render and cannot be grabbed again. */
export const MIN_DURATION_MINUTES = 5;

/** How long a finger must rest before the block starts following it. */
const LONG_PRESS_MS = 350;

/** Finger travel before the long press is abandoned as a page scroll. */
const SCROLL_SLOP_PX = 10;

/**
 * Mouse travel before a press on the body counts as a drag, not a click.
 *
 * Eight pixels rather than the four a click threshold usually needs, because
 * the block snaps to five minutes and five minutes is nine pixels at the
 * timeline's scale. A smaller threshold has a band where the press has armed
 * and the nearest snap is already a five-minute jump — so an ordinary wobble
 * while clicking would quietly reschedule the block. At eight the two line up:
 * under it nothing happens, over it the block has genuinely been dragged.
 */
const DRAG_SLOP_PX = 8;

/**
 * A click follows the pointerup that ends a drag. This is how long afterwards
 * that click is ignored — long enough to swallow it, short enough that a
 * genuine tap right after a drag still lands.
 */
const CLICK_SUPPRESSION_MS = 300;

const snapTo = (minute: number) =>
  Math.round(minute / SNAP_MINUTES) * SNAP_MINUTES;

export function spansEqual(a: Span, b: Span): boolean {
  return (
    a.startMinute === b.startMinute && a.durationMinutes === b.durationMinutes
  );
}

/**
 * The block's span after dragging `deltaMinutes` from where the gesture began.
 *
 * Always measured from the original span rather than accumulated per move, so
 * a drag out past the end of the day and back lands where the pointer is
 * rather than somewhere the clamping left it.
 *
 * Resizing holds the opposite edge still: dragging the top edge changes when
 * the block starts without changing when it ends, and vice versa.
 */
export function applyGesture(
  origin: Span,
  mode: GestureMode,
  deltaMinutes: number,
): Span {
  const originEnd = origin.startMinute + origin.durationMinutes;

  if (mode === "move") {
    const startMinute = Math.max(
      0,
      Math.min(
        1440 - origin.durationMinutes,
        snapTo(origin.startMinute + deltaMinutes),
      ),
    );
    return { startMinute, durationMinutes: origin.durationMinutes };
  }

  if (mode === "resize-start") {
    const startMinute = Math.max(
      0,
      Math.min(
        originEnd - MIN_DURATION_MINUTES,
        snapTo(origin.startMinute + deltaMinutes),
      ),
    );
    return { startMinute, durationMinutes: originEnd - startMinute };
  }

  const endMinute = Math.max(
    origin.startMinute + MIN_DURATION_MINUTES,
    // Not snapMinute(), which clamps to the last on-grid minute of the day:
    // a block may legitimately end at midnight, only not start there.
    Math.min(1440, snapTo(originEnd + deltaMinutes)),
  );
  return {
    startMinute: origin.startMinute,
    durationMinutes: endMinute - origin.startMinute,
  };
}

export interface ActiveGesture extends Span {
  id: string;
  mode: GestureMode;
}

interface Options {
  pxPerMinute: number;
  onCommit: (id: string, span: Span) => void;
}

export function useBlockGesture({ pxPerMinute, onCommit }: Options) {
  const [active, setActive] = useState<ActiveGesture | null>(null);
  const suppressed = useRef<{ id: string; until: number } | null>(null);

  /**
   * Teardown for the gesture in flight, if any.
   *
   * A gesture normally ends on its own pointerup. Two things can stop that
   * from arriving: navigating away mid-drag, which unmounts the timeline, and
   * a second press starting before the first one finished. Both would
   * otherwise leave the window listeners — one of them a non-passive
   * `touchmove` that suppresses scrolling — attached for the rest of the
   * session.
   */
  const teardown = useRef<((commit: boolean) => void) | null>(null);

  useEffect(() => () => teardown.current?.(false), []);

  /**
   * True for the block that was just dragged. The body is a button, so the
   * pointerup ending a drag is followed by a click — without this, every move
   * would also open the editor.
   *
   * Scoped to that one block, not to the timeline: letting go of one block and
   * immediately tapping another is a normal thing to do, and swallowing that
   * second tap looks like the app ignoring you.
   */
  const shouldSuppressClick = (id: string) =>
    suppressed.current?.id === id && Date.now() < suppressed.current.until;

  const begin = (
    id: string,
    origin: Span,
    mode: GestureMode,
    event: React.PointerEvent,
  ) => {
    // Leave the right button to the context menu.
    if (event.pointerType === "mouse" && event.button !== 0) return;

    // A second finger landing on another block must not join the gesture the
    // first one is driving. Abandon the old one rather than running both.
    teardown.current?.(false);

    const { pointerId } = event;
    const originY = event.clientY;
    const isTouch = event.pointerType !== "mouse";
    let armed = false;
    let current = origin;
    let longPress: number | undefined;

    const arm = () => {
      armed = true;
      setActive({ id, mode, ...origin });
    };

    const onPointerMove = (moveEvent: PointerEvent) => {
      if (moveEvent.pointerId !== pointerId) return;
      const deltaY = moveEvent.clientY - originY;

      if (!armed) {
        // Touch on the body: the long press has not fired yet, or `armed`
        // would be set. Movement now is the page being scrolled.
        if (isTouch && mode === "move") {
          if (Math.abs(deltaY) > SCROLL_SLOP_PX) finish(false);
          return;
        }
        if (Math.abs(deltaY) < DRAG_SLOP_PX) return;
        arm();
      }

      current = applyGesture(origin, mode, deltaY / pxPerMinute);
      setActive({ id, mode, ...current });
    };

    /**
     * Touch only, and non-passive so `preventDefault` still counts. The long
     * press means the page has not started scrolling yet, which is the one
     * window in which cancelling the scroll works at all.
     */
    const holdStill = (touchEvent: TouchEvent) => {
      if (armed && touchEvent.cancelable) touchEvent.preventDefault();
    };

    const finish = (commit: boolean) => {
      if (teardown.current === finish) teardown.current = null;
      window.clearTimeout(longPress);
      window.removeEventListener("pointermove", onPointerMove);
      window.removeEventListener("pointerup", onPointerUp);
      window.removeEventListener("pointercancel", onCancel);
      window.removeEventListener("touchmove", holdStill);

      // Only a drag that changed something swallows the click. A press that
      // ends where it started is a tap, however far the pointer wandered in
      // between, and should still open the editor.
      if (commit && armed && !spansEqual(current, origin)) {
        suppressed.current = { id, until: Date.now() + CLICK_SUPPRESSION_MS };
        onCommit(id, current);
      }

      armed = false;
      setActive(null);
    };

    const onPointerUp = (upEvent: PointerEvent) => {
      if (upEvent.pointerId === pointerId) finish(true);
    };
    const onCancel = (cancelEvent: PointerEvent) => {
      if (cancelEvent.pointerId === pointerId) finish(false);
    };

    teardown.current = finish;

    // Only the body on touch waits for a long press. Everything else arms on
    // travel alone — including the resize handles, which have no competing
    // meaning but would otherwise commit a five-minute change from a click
    // that drifted a few pixels.
    if (mode === "move" && isTouch) {
      longPress = window.setTimeout(arm, LONG_PRESS_MS);
    }

    window.addEventListener("pointermove", onPointerMove);
    window.addEventListener("pointerup", onPointerUp);
    window.addEventListener("pointercancel", onCancel);
    window.addEventListener("touchmove", holdStill, { passive: false });
  };

  /**
   * Keyboard equivalent of a resize drag, in one snap step.
   *
   * `origin` comes from the last span the server sent back, and the mutation
   * has no optimistic update, so a repeat that arrives before the round trip
   * recomputes the same result. The caller drops auto-repeat for that reason;
   * this stays the one-step-per-press primitive.
   */
  const nudge = (id: string, origin: Span, mode: GestureMode, steps: number) => {
    const next = applyGesture(origin, mode, steps * SNAP_MINUTES);
    if (!spansEqual(next, origin)) onCommit(id, next);
  };

  return { active, begin, nudge, shouldSuppressClick };
}
